import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatService } from '../../services/chatService';

interface TagSuggestion {
  tag: string;
  confidence: number;
  confidence_level: string;
  alternatives?: { tag: string; score: number }[];
  reasoning?: string;
  chunk_count?: number;
  homogeneity?: number;
}

interface BatchFileConfig {
  metadata_tags: Record<string, string>;
  as_of_date: string;
  parsing_strategy: string;
  strategy_options: string[];
  strategy_read_only: boolean;
  auto_suggest_tag: boolean;
}

const FALLBACK_POLICY: Record<string, { options: string[]; readOnly: boolean }> = {
  pdf: { options: ['Normal', 'Complex'], readOnly: false },
  word: { options: ['Normal', 'Complex'], readOnly: false },
  excel: { options: ['Normal', 'Complex'], readOnly: false },
  ppt: { options: ['Complex'], readOnly: true },
  email: { options: ['Email'], readOnly: true },
};

const getExt = (filename: string): string => {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
};

const getKindFromExt = (ext: string): keyof typeof FALLBACK_POLICY | 'unknown' => {
  if (ext === '.pdf') return 'pdf';
  if (['.doc', '.docx', '.docm', '.dot', '.dotx', '.dotm', '.rtf'].includes(ext)) return 'word';
  if (['.xls', '.xlsx', '.xlsm', '.xlt', '.xltx', '.xltm', '.xlsb', '.csv'].includes(ext)) return 'excel';
  if (['.ppt', '.pptx', '.pptm', '.pot', '.potx', '.potm', '.pps', '.ppsx', '.ppsm'].includes(ext)) return 'ppt';
  if (ext === '.msg') return 'email';
  return 'unknown';
};

const getFallbackStrategyState = (filename: string) => {
  const ext = getExt(filename);
  // Mirrors backend allowed_ui_strategies: .xlsm is locked to Complex.
  if (ext === '.xlsm') {
    return {
      parsing_strategy: 'Complex',
      strategy_options: ['Complex'],
      strategy_read_only: true,
    };
  }
  const kind = getKindFromExt(ext);
  const policy = kind !== 'unknown' ? FALLBACK_POLICY[kind] : FALLBACK_POLICY.pdf;
  return {
    parsing_strategy: policy.options[0],
    strategy_options: policy.options,
    strategy_read_only: policy.readOnly,
  };
};

const KnowledgeFileUpload: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [files, setFiles] = useState<File[]>([]);
  const [tag, setTag] = useState('');
  const [type, setType] = useState('');
  const [asOfDate, setAsOfDate] = useState(today);
  const [tagFields, setTagFields] = useState<Record<string, string[]>>({});
  const [defaultTagField, setDefaultTagField] = useState('RAG_tag');
  const [metadataTags, setMetadataTags] = useState<Record<string, string>>({});
  const [batchConfigs, setBatchConfigs] = useState<BatchFileConfig[]>([]);
  const [strategy, setStrategy] = useState('Normal');
  const [strategyOptions, setStrategyOptions] = useState<string[]>(['Normal', 'Complex']);
  const [strategyReadOnly, setStrategyReadOnly] = useState(false);
  const [autoTag, setAutoTag] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [typeList, setTypeList] = useState<string[]>([]);

  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [fileUuid, setFileUuid] = useState('');
  const [batchId, setBatchId] = useState('');
  const [batchProgress, setBatchProgress] = useState<{
    total: number;
    completed: number;
    failed: number;
    status: string;
  } | null>(null);
  const [suggestion, setSuggestion] = useState<TagSuggestion | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isBatchMode = files.length > 1;

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const meta = await chatService.getChatMeta();
        setTags(meta.tags || []);
        setTypeList(meta.type_list || []);
        setTagFields(meta.tag_fields || {});
        setDefaultTagField(meta.default_tag_field || 'RAG_tag');
        if (meta.tags?.length) setTag(meta.tags[0]);
        if (meta.type_list?.length) setType(meta.type_list[0]);
        const initialMetadata: Record<string, string> = {};
        Object.entries(meta.tag_fields || {}).forEach(([field, values]) => {
          if (Array.isArray(values) && values.length > 0) {
            initialMetadata[field] = values[0];
          }
        });
        setMetadataTags(initialMetadata);
      } catch {
        // fallback
      }
    };
    loadMeta();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (!files.length || batchConfigs.length !== files.length || Object.keys(tagFields).length === 0) return;
    setBatchConfigs((prev) =>
      prev.map((cfg) => {
        const nextMeta = { ...cfg.metadata_tags };
        Object.entries(tagFields).forEach(([field, values]) => {
          if (!nextMeta[field] && Array.isArray(values) && values.length > 0) {
            nextMeta[field] = values[0];
          }
        });
        return { ...cfg, metadata_tags: nextMeta };
      }),
    );
  }, [files, batchConfigs.length, tagFields]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    const selectedFiles = Array.from(selected);
    setFiles(selectedFiles);
    const initialBatchConfigs = selectedFiles.map((file) => {
      const fallbackPolicy = getFallbackStrategyState(file.name);
      const initial: Record<string, string> = {};
      Object.entries(tagFields).forEach(([field, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          initial[field] = values[0];
        }
      });
      if (!Object.keys(tagFields).length && tag) {
        initial[defaultTagField] = tag;
      }
      return {
        metadata_tags: initial,
        as_of_date: today,
        parsing_strategy: fallbackPolicy.parsing_strategy,
        strategy_options: fallbackPolicy.strategy_options,
        strategy_read_only: fallbackPolicy.strategy_read_only,
        auto_suggest_tag: autoTag,
      };
    });
    setBatchConfigs(initialBatchConfigs);
    if (selectedFiles.length === 1) {
      const firstPolicy = getFallbackStrategyState(selectedFiles[0].name);
      setStrategyOptions(firstPolicy.strategy_options);
      setStrategy(firstPolicy.parsing_strategy);
      setStrategyReadOnly(firstPolicy.strategy_read_only);
    }
    setSuggestion(null);
    setFileUuid('');
    setBatchId('');
    setBatchProgress(null);
    setError('');
    setSuccess('');
    setStatusMsg('');

    const loadPolicy = async () => {
      if (selectedFiles.length === 1) {
        try {
          const policy = await chatService.getKnowledgeStrategyOptions(selectedFiles[0].name);
          const options = Array.isArray(policy?.allowed_strategies) ? policy.allowed_strategies : ['Normal', 'Complex'];
          const defaultStrategy = policy?.default_strategy || options[0] || 'Normal';
          setStrategyOptions(options);
          setStrategy(defaultStrategy);
          setStrategyReadOnly(options.length === 1);
        } catch {
          // fallback already applied
        }
        return;
      }

      const resolvedPolicies = await Promise.all(
        selectedFiles.map(async (file) => {
          try {
            const policy = await chatService.getKnowledgeStrategyOptions(file.name);
            const options = Array.isArray(policy?.allowed_strategies) ? policy.allowed_strategies : ['Normal', 'Complex'];
            const defaultStrategy = policy?.default_strategy || options[0] || 'Normal';
            return {
              parsing_strategy: defaultStrategy,
              strategy_options: options,
              strategy_read_only: options.length === 1,
            };
          } catch {
            return getFallbackStrategyState(file.name);
          }
        }),
      );

      setBatchConfigs((prev) =>
        prev.map((cfg, idx) => ({
          ...cfg,
          parsing_strategy: resolvedPolicies[idx]?.parsing_strategy || cfg.parsing_strategy,
          strategy_options: resolvedPolicies[idx]?.strategy_options || cfg.strategy_options,
          strategy_read_only: resolvedPolicies[idx]?.strategy_read_only ?? cfg.strategy_read_only,
        })),
      );
    };

    loadPolicy();
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setBatchConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  const pollStatus = useCallback((uuid: string) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 300) {
        if (pollRef.current) clearInterval(pollRef.current);
        setProcessing(false);
        setStatusMsg('Processing timed out');
        return;
      }
      try {
        const status = await chatService.getKnowledgeFileStatus(uuid);
        setStatusMsg(`Status: ${status.status}`);
        if (status.status === 'success') {
          if (pollRef.current) clearInterval(pollRef.current);
          setProcessing(false);
          setSuccess('File ingested successfully!');
          setFiles([]);
          setSuggestion(null);
        } else if (status.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setProcessing(false);
          setError(`Processing failed: ${status.error_message || 'Unknown error'}`);
        } else if (status.status === 'awaiting_tag_confirmation' && status.suggestion) {
          // Auto-tag finished in the background; switch UI to confirm-tag step.
          if (pollRef.current) clearInterval(pollRef.current);
          setProcessing(false);
          setStatusMsg('');
          setFileUuid(uuid);
          setSuggestion(status.suggestion);
          setAvailableTags(status.available_tags || []);
          setSelectedTag(status.suggestion?.tag || '');
        }
      } catch {
        // keep polling
      }
    }, 3000);
  }, []);

  const pollBatchStatus = useCallback((id: string) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 600) {
        if (pollRef.current) clearInterval(pollRef.current);
        setProcessing(false);
        setStatusMsg('Batch processing timed out');
        return;
      }
      try {
        const status = await chatService.getBatchStatus(id);
        setBatchProgress({
          total: status.total_files,
          completed: status.completed_files,
          failed: status.failed_files,
          status: status.status,
        });
        const done = status.completed_files + status.failed_files;
        setStatusMsg(`Processing: ${done}/${status.total_files} files`);

        if (status.status !== 'processing') {
          if (pollRef.current) clearInterval(pollRef.current);
          setProcessing(false);
          if (status.status === 'success') {
            setSuccess(`All ${status.total_files} files ingested successfully!`);
          } else if (status.status === 'failed') {
            setError(`Batch failed: all ${status.total_files} files failed.`);
          } else {
            setSuccess(
              `Batch complete: ${status.completed_files} succeeded, ${status.failed_files} failed.`,
            );
          }
          setFiles([]);
        }
      } catch {
        // keep polling
      }
    }, 3000);
  }, []);

  const handleUploadAndProcess = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError('');
    setSuccess('');
    setStatusMsg('');
    setBatchProgress(null);

    try {
      if (isBatchMode) {
        setStatusMsg('Uploading files...');
        await chatService.uploadKnowledgeFilesBatch(files);

        setStatusMsg('Starting batch ingestion...');
        const response = await chatService.addKnowledgeFilesBatch(
          files.map((f) => f.name),
          {
            tag: undefined,
            type: type,
            auto_suggest_tag: batchConfigs.length > 0 ? batchConfigs.every((cfg) => cfg.auto_suggest_tag) : false,
            files: files.map((f, idx) => {
              const cfg = batchConfigs[idx] || {
                metadata_tags: {},
                as_of_date: today,
                parsing_strategy: 'Normal',
                strategy_options: ['Normal', 'Complex'],
                strategy_read_only: false,
                auto_suggest_tag: autoTag,
              };
              const fileTag = cfg.metadata_tags[defaultTagField] || '';
              return {
                filename: f.name,
                tag: cfg.auto_suggest_tag ? undefined : fileTag,
                as_of_date: cfg.as_of_date || today,
                parsing_strategy: cfg.parsing_strategy || 'Normal',
                auto_suggest_tag: cfg.auto_suggest_tag,
                metadata_tags: cfg.metadata_tags,
              };
            }),
          },
        );

        const id = response.data?.batch_id || response.batch_id;
        if (id) {
          setBatchId(id);
          setProcessing(true);
          pollBatchStatus(id);
        } else {
          setSuccess('Batch processing started');
        }
      } else {
        const file = files[0];
        await chatService.uploadKnowledgeFile(file);

        const response = await chatService.addKnowledgeFile(file.name, {
          tag: autoTag ? undefined : tag,
          type,
          parsing_strategy: strategy,
          auto_suggest_tag: autoTag,
          as_of_date: asOfDate,
          metadata_tags: metadataTags,
        });

        if (autoTag && response.data?.status === 'awaiting_tag_confirmation') {
          setFileUuid(response.data.file_uuid);
          setSuggestion(response.data.suggestion);
          setAvailableTags(response.data.available_tags || []);
          setSelectedTag(response.data.suggestion?.tag || '');
        } else {
          const uuid = response.data?.file_uuid || response.file_uuid;
          if (uuid) {
            setFileUuid(uuid);
            setProcessing(true);
            pollStatus(uuid);
          } else {
            setSuccess('File processing started');
          }
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmTag = async (overrode: boolean) => {
    if (!fileUuid || !selectedTag) return;
    setError('');
    try {
      await chatService.confirmTag(fileUuid, selectedTag, overrode);
      setSuggestion(null);
      setProcessing(true);
      setStatusMsg('Processing started...');
      pollStatus(fileUuid);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Confirm failed');
    }
  };

  const confidenceColor = (level: string) => {
    switch (level) {
      case 'high': return 'text-green-600 bg-green-50 border-green-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-red-600 bg-red-50 border-red-200';
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-700">Upload Knowledge Files</h3>

      {/* File picker */}
      <div
        className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.docm,.dot,.dotx,.dotm,.rtf,.xls,.xlsx,.xlsm,.xlt,.xltx,.xltm,.xlsb,.ppt,.pptx,.pptm,.pot,.potx,.potm,.pps,.ppsx,.ppsm,.msg"
          onChange={handleFileChange}
          className="hidden"
        />
        <p className="text-sm text-gray-500">
          <span className="text-blue-600 font-medium">Choose files</span> or drag and drop here
        </p>
        <p className="text-xs text-gray-400 mt-1">PDF, DOCX, XLSX, PPTX, MSG — select multiple files for batch ingestion</p>
      </div>

      {/* File list */}
      {files.length > 0 && !suggestion && !processing && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="text-gray-700 truncate max-w-[300px]">{f.name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                className="text-gray-400 hover:text-red-500 text-xs ml-2"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Options */}
      {files.length > 0 && !suggestion && !processing && (
        <div className="space-y-3">
          {isBatchMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
              Batch mode: {files.length} files selected.
            </div>
          )}
          {!isBatchMode && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoTag}
                onChange={(e) => setAutoTag(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
              />
              <span className="text-sm text-gray-600">Auto-suggest tag</span>
            </label>
          )}
          <div className="grid grid-cols-2 gap-4">
            {!isBatchMode && !autoTag && Object.keys(tagFields).length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{defaultTagField} *</label>
                <select
                  value={metadataTags[defaultTagField] || tag}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTag(v);
                    setMetadataTags((prev) => ({ ...prev, [defaultTagField]: v }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {(tagFields[defaultTagField] || tags).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}
            {!isBatchMode && !autoTag && Object.keys(tagFields).length === 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tag *</label>
                <select
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {tags.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}
            {!isBatchMode && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">As of date *</label>
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            )}
            {typeList.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {typeList.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}
            {!isBatchMode && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Parsing Strategy</label>
                {strategyReadOnly ? (
                  <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">
                    {strategy} (auto)
                  </div>
                ) : (
                  <select
                    value={strategy}
                    onChange={(e) => setStrategy(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {strategyOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {isBatchMode && (
            <div className="space-y-2 rounded-lg border border-gray-200 p-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-600">Batch metadata per file</p>
              {files.map((f, idx) => {
                const cfg = batchConfigs[idx] || {
                  metadata_tags: {},
                  as_of_date: today,
                  parsing_strategy: 'Normal',
                  strategy_options: ['Normal', 'Complex'],
                  strategy_read_only: false,
                  auto_suggest_tag: autoTag,
                };
                const visibleTagFields = Object.entries(tagFields).filter(
                  ([field]) => !(cfg.auto_suggest_tag && field === defaultTagField),
                );
                return (
                  <div key={`${f.name}-${idx}`} className="rounded-md bg-white border border-gray-200 p-2 space-y-2">
                    <p className="text-xs text-gray-700 truncate">{f.name}</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cfg.auto_suggest_tag}
                        onChange={(e) =>
                          setBatchConfigs((prev) =>
                            prev.map((item, i) =>
                              i === idx ? { ...item, auto_suggest_tag: e.target.checked } : item,
                            ),
                          )
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                      />
                      <span className="text-xs text-gray-600">Auto-suggest tag for this file</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {visibleTagFields.map(([field, values]) => (
                        <div key={`${idx}-${field}`}>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">{field}</label>
                          <select
                            value={cfg.metadata_tags[field] || ''}
                            onChange={(e) =>
                              setBatchConfigs((prev) =>
                                prev.map((item, i) =>
                                  i === idx
                                    ? {
                                        ...item,
                                        metadata_tags: {
                                          ...item.metadata_tags,
                                          [field]: e.target.value,
                                        },
                                      }
                                    : item,
                                ),
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                          >
                            {(values || []).map((v) => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                      {Object.keys(tagFields).length === 0 && !cfg.auto_suggest_tag && (
                        <div>
                          <label className="block text-[11px] font-medium text-gray-500 mb-1">Tag</label>
                          <select
                            value={cfg.metadata_tags[defaultTagField] || ''}
                            onChange={(e) =>
                              setBatchConfigs((prev) =>
                                prev.map((item, i) =>
                                  i === idx
                                    ? {
                                        ...item,
                                        metadata_tags: {
                                          ...item.metadata_tags,
                                          [defaultTagField]: e.target.value,
                                        },
                                      }
                                    : item,
                                ),
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                          >
                            {tags.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">As of date</label>
                        <input
                          type="date"
                          value={cfg.as_of_date || today}
                          onChange={(e) =>
                            setBatchConfigs((prev) =>
                              prev.map((item, i) =>
                                i === idx ? { ...item, as_of_date: e.target.value } : item,
                              ),
                            )
                          }
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-500 mb-1">Parsing Strategy</label>
                        {cfg.strategy_read_only ? (
                          <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-2 py-1.5 text-xs text-gray-600">
                            {cfg.parsing_strategy} (auto)
                          </div>
                        ) : (
                          <select
                            value={cfg.parsing_strategy}
                            onChange={(e) =>
                              setBatchConfigs((prev) =>
                                prev.map((item, i) =>
                                  i === idx ? { ...item, parsing_strategy: e.target.value } : item,
                                ),
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                          >
                            {cfg.strategy_options.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={handleUploadAndProcess}
            disabled={
              uploading ||
              (isBatchMode &&
                batchConfigs.some(
                  (cfg) =>
                    !cfg.auto_suggest_tag &&
                    !(cfg.metadata_tags[defaultTagField] || '').trim(),
                ))
            }
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            {uploading
              ? 'Uploading...'
              : isBatchMode
                ? `Upload & Process ${files.length} Files`
                : 'Upload & Process'}
          </button>
        </div>
      )}

      {/* Auto-tag suggestion (single file only) */}
      {suggestion && (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">Auto-Tag Suggestion</h4>

          <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${confidenceColor(suggestion.confidence_level)}`}>
            {suggestion.confidence_level} confidence ({(suggestion.confidence * 100).toFixed(0)}%)
          </div>

          <p className="text-sm text-gray-600">{suggestion.reasoning}</p>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Selected Tag</label>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {availableTags.map((t) => (
                <option key={t} value={t}>
                  {t}{t === suggestion.tag ? ' (suggested)' : ''}
                </option>
              ))}
            </select>
          </div>

          {suggestion.alternatives && suggestion.alternatives.length > 0 && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">Alternatives:</span>{' '}
              {suggestion.alternatives.map((a, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedTag(a.tag)}
                  className="inline-block mr-2 px-2 py-0.5 bg-gray-100 rounded hover:bg-blue-100 transition-colors"
                >
                  {a.tag} ({(a.score * 100).toFixed(0)}%)
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => handleConfirmTag(selectedTag !== suggestion.tag)}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Confirm: {selectedTag}
            </button>
          </div>
        </div>
      )}

      {/* Processing indicator */}
      {processing && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500 mt-2">{statusMsg || 'Processing...'}</p>
          {batchProgress && (
            <div className="mt-3 w-full">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{batchProgress.completed + batchProgress.failed} / {batchProgress.total}</span>
                {batchProgress.failed > 0 && (
                  <span className="text-red-500">{batchProgress.failed} failed</span>
                )}
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all"
                  style={{
                    width: `${((batchProgress.completed + batchProgress.failed) / batchProgress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{success}</div>}
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default KnowledgeFileUpload;
